import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useMemo, useState } from 'react';
import './expandabe-table-view-icon-rtl.css';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AddNewExamDialog } from './components/add-new-exam-dialog';
import globalProgramStore from '../../../../stores/program-store';
import globalCourseStore from '../../../../stores/global-course-store';
import dateFormat from '../../../../utils/date-format';
import { ExamDetailsPage } from './components/exam-page/exam-details-page';
import { Steps } from 'primereact/steps';
import { MenuItem } from 'primereact/menuitem';
import TaskIcon from '../../../../assets/icons/icon-doc-blue.svg';
import {
  ExamMetadata,
  ExamSessionFile,
} from '../../../../api/types/programs/course/exams/response/exam-metadata';
import { deleteExamOperation } from './operations/delete-exam-operation';

export interface CourseExamsDatamodel {
  isProfessor: boolean;
}

export const CourseExams: React.FC<CourseExamsDatamodel> = ({
  isProfessor,
}) => {
  const [addNewExam, setAddNewExam] = useState(false);
  const [rowIndexSelected, setRowIndexSelected] = useState<
    number | undefined
  >();
  const [expandedRows, setExpandedRows] = useState(null);

  // Sample data
  const items: MenuItem[] = [
    {
      label: 'המבחן נוצר',
    },
    {
      label: 'שיבוץ לכיתות',
    },
    {
      label: 'שיבוץ משגיחים',
    },

    {
      label: 'ממתין לבחינה',
    },

    {
      label: 'הבחינה התחילה (טרם הסתיים)',
    },

    {
      label: 'הבחינה התבצעה',
    },
    {
      label: 'מחברות נאספו לבדיקה',
    },

    {
      label: 'הוזנו ציונים',
    },
  ];
  // Row expansion template
  const rowExpansionTemplate = (data) => {
    return (
      <div
        style={{
          marginTop: '16px',
          marginBottom: '16px',
          marginRight: '16px',
          marginLeft: '16px',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            color: '#4b5563',
            textAlign: 'right',
            marginBottom: '8px',
          }}
        >
          סטטוס בחינה
        </div>
        <Steps style={{ direction: 'ltr' }} model={items} activeIndex={3} />
      </div>
    );
  };

  // Custom expander template with rotation
  const expanderTemplate = (data, options) => {
    const togglerStyle = {
      transform: options.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s',
    };

    return (
      <button
        className={options.className}
        onClick={options.onClick}
        style={togglerStyle}
      >
        {options.children}
      </button>
    );
  };

  const programId = useMemo(() => {
    return globalProgramStore.getCurrentId();
  }, [globalProgramStore.currentId]);

  const courseId = useMemo(() => {
    return globalCourseStore.currentId;
  }, [globalCourseStore.currentId]);

  const handleHideDialog = () => setRowIndexSelected(undefined);

  const currentExams = globalCourseStore.getCurrentCourse().details.exams;
  const isAlreadyHundredCompletionGradeTaken = currentExams.some(
    (item) => item.completionGrade,
  );

  const handleDelete = async (assignmentId: number) => {
    const currentProgramId = globalProgramStore.getCurrentId();
    const currentCourseId = globalCourseStore.getCurrentCourse()?.metadata.id;

    await deleteExamOperation(currentProgramId, currentCourseId, assignmentId);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <img src={TaskIcon} />
          <div
            style={{
              color: '#041E49',
              fontSize: '16px',
              marginRight: '8px',
            }}
          >
            מבחנים
          </div>
        </div>

        <Button
          style={{ height: '30px' }}
          label={'להוספה'}
          visible={!isProfessor}
          onClick={() => setAddNewExam(true)}
        />
      </div>
      <Dialog
        header="הוספת מבחן"
        visible={addNewExam}
        style={{
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
          width: '400px',
        }}
        onHide={() => setAddNewExam(false)}
      >
        <AddNewExamDialog
          programId={programId}
          courseId={courseId}
          onHide={() => setAddNewExam(false)}
          isAlreadyHundredCompletionGradeTaken={
            isAlreadyHundredCompletionGradeTaken
          }
        />
      </Dialog>

      {rowIndexSelected !== undefined && (
        <ExamDetailsPage
          examDetails={currentExams[rowIndexSelected]}
          onHide={handleHideDialog}
        />
      )}

      <DataTable
        style={{ direction: 'rtl', marginTop: '8px' }}
        value={currentExams}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        onRowClick={(event: DataTableRowClickEvent) => {
          setRowIndexSelected(event.index);
        }}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column field="examName" header="שם" style={{ textAlign: 'right' }} />
        <Column
          field="examTypeName"
          header="סוג מבחן"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="examEntity.points"
          header="משקל"
          style={{ textAlign: 'right' }}
          body={(exam: ExamMetadata) => (
            <label>{exam.completionGrade ? 'משלים למאה' : exam.points}</label>
          )}
        />
        <Column
          header="מועד א'"
          style={{ textAlign: 'right' }}
          body={(exam: ExamMetadata) => {
            const firstSession = exam.sessions[0];
            if (!firstSession) {
              return (
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  טרם נוצר מועד זה
                </span>
              );
            }
            if (
              firstSession.averageGrade === null ||
              firstSession.averageGrade === undefined
            ) {
              return (
                <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                  טרם הוזנו ציונים לבחינה
                </span>
              );
            }
            const gradeColor =
              firstSession.averageGrade >= 60 ? '#059669' : '#dc2626';
            return (
              <span style={{ color: gradeColor, fontWeight: '600' }}>
                {firstSession.averageGrade}
              </span>
            );
          }}
        />
        <Column
          header="מועד ב'"
          style={{ textAlign: 'right' }}
          body={(exam: ExamMetadata) => {
            const secondSession = exam.sessions[1];
            if (!secondSession) {
              return (
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  טרם נוצר מועד זה
                </span>
              );
            }
            if (
              secondSession.averageGrade === null ||
              secondSession.averageGrade === undefined
            ) {
              return (
                <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                  טרם הוזנו ציונים לבחינה
                </span>
              );
            }
            const gradeColor =
              secondSession.averageGrade >= 60 ? '#059669' : '#dc2626';
            return (
              <span style={{ color: gradeColor, fontWeight: '600' }}>
                {secondSession.averageGrade}
              </span>
            );
          }}
        />
        <Column
          header="מועד ג'"
          style={{ textAlign: 'right' }}
          body={(exam: ExamMetadata) => {
            const thirdSession = exam.sessions[2];
            if (!thirdSession) {
              return (
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  טרם נוצר מועד זה
                </span>
              );
            }
            if (
              thirdSession.averageGrade === null ||
              thirdSession.averageGrade === undefined
            ) {
              return (
                <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                  טרם הוזנו ציונים לבחינה
                </span>
              );
            }
            const gradeColor =
              thirdSession.averageGrade >= 60 ? '#059669' : '#dc2626';
            return (
              <span style={{ color: gradeColor, fontWeight: '600' }}>
                {thirdSession.averageGrade}
              </span>
            );
          }}
        />
        <Column
          field="examEntity.examDate"
          header="תאריך יצירה"
          style={{ textAlign: 'right' }}
          body={(rowData: ExamMetadata) => (
            <div>{dateFormat.displayFormatDate(rowData.examCreatedAt)}</div>
          )}
        />
        <Column expander={true} style={{ width: '3em' }} />

        <Column
          style={{ textAlign: 'right' }}
          field="נשלח ע״י"
          header=""
          body={(rowData: ExamSessionFile) => (
            <Button
              icon="pi pi-trash"
              text
              severity="danger"
              onClick={() => handleDelete(rowData.id)}
            />
          )}
        />
      </DataTable>
    </div>
  );
};
